import { useEffect, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Trash2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface NetworkOption {
  id: string;
  name: string;
}

interface Props {
  brokerUserId: string;
  brokerLabel: string;
  /** All of the shipper's networks — scope of the "entire network" removal. */
  networks: NetworkOption[];
  /** Shipper company name for the notification body. */
  shipperName: string;
  onRemoved: () => void;
}

/**
 * Destructive control: fully removes a broker from the shipper's network.
 *
 * - Flips every 'accepted' membership row across ALL of the shipper's groups
 *   to 'removed'.
 * - Fires ONE `lr_direct_network_removed` notification (now accurate).
 */
export function RemoveBrokerFromNetworkDialog({
  brokerUserId,
  brokerLabel,
  networks,
  shipperName,
  onRemoved,
}: Props) {
  const [open, setOpen] = useState(false);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [memberOf, setMemberOf] = useState<NetworkOption[]>([]);

  useEffect(() => {
    if (!open) { setAck(false); return; }
    let cancelled = false;
    (async () => {
      const ids = networks.map((n) => n.id);
      if (ids.length === 0) { setMemberOf([]); return; }
      const { data } = await supabase
        .from("shipper_network_brokers" as any)
        .select("network_id")
        .in("network_id", ids)
        .eq("broker_user_id", brokerUserId)
        .eq("status", "accepted");
      if (cancelled) return;
      const set = new Set<string>(((data as any[]) || []).map((r) => r.network_id));
      setMemberOf(networks.filter((n) => set.has(n.id)));
    })();
    return () => { cancelled = true; };
  }, [open, brokerUserId, networks]);

  const handleConfirm = async () => {
    if (!ack) return;
    const ids = networks.map((n) => n.id);
    if (ids.length === 0) { setOpen(false); return; }
    setBusy(true);

    // Routed through the authorized edge function so any shipper company
    // teammate (not just the network's original creator) can remove brokers.
    // The function mirrors respond-network-request's company_users check.
    const { data, error } = await supabase.functions.invoke("manage-network-membership", {
      body: { action: "remove_broker", brokerUserId: brokerUserId, networkIds: ids },
    });
    const err = error || (data as any)?.error;
    if (err) {
      setBusy(false);
      const msg = typeof err === "string" ? err : (err.message || "Failed to remove broker");
      toast.error(msg);
      return;
    }

    setBusy(false);
    setOpen(false);
    setAck(false);
    toast.success("Broker removed from your network");
    onRemoved();
  };


  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
        title="Remove from network"
        onClick={() => setOpen(true)}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>

      <AlertDialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setAck(false); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {brokerLabel} from your network?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  They will no longer see any of your private loads and cannot bid on
                  your direct posts. Their offer and load history stays for your records.
                </p>
                {memberOf.length > 0 && (
                  <div className="rounded-md border border-border bg-muted/30 p-2">
                    <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-1">
                      Will be removed from
                    </div>
                    <ul className="text-xs list-disc pl-4 space-y-0.5">
                      {memberOf.map((n) => (
                        <li key={n.id}>{n.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-muted-foreground">This cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <label className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 cursor-pointer">
            <Checkbox
              checked={ack}
              onCheckedChange={(v) => setAck(v === true)}
              className="mt-0.5"
            />
            <span className="text-sm leading-snug">
              I understand this fully removes the broker from my network.
            </span>
          </label>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!ack || busy}
              onClick={(e) => { e.preventDefault(); if (ack) handleConfirm(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:pointer-events-none"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remove from network"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
